import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Sparkles, ChevronDown, CheckCircle, XCircle,
  ArrowRight, RotateCcw, Zap, Brain, Target, TrendingUp, BookOpen
} from 'lucide-react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { resolveStudyTarget, normalStudyName } from '../lib/studyTarget';
import { useAuth } from '../contexts/AuthContext';
import { MathText } from './MathRenderer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFF_COLORS = { easy: '#10b981', medium: '#f59e0b', hard: '#ef4444' };

const sel = 'w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-white text-sm font-body focus:outline-none focus:border-cyan-500/50 appearance-none';

export default function QuizArena() {
  const { user } = useAuth();
  const location = useLocation();
  const appliedFocus = useRef(null);
  const [focusNotice, setFocusNotice] = useState('');
  const userClass = user?.class_level || '9';
  const isNios = user?.school === 'nios';

  // Restore saved quiz state from localStorage
  const _saved = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem('aceit_quiz_state') || 'null');
      // A completed result must not intercept a new practice link from Progress.
      return location.state?.studyFocus && saved?.phase === 'result' ? null : saved;
    } catch { return null; }
  })();

  const [subjects, setSubjects]     = useState([]);
  const [chapters, setChapters]     = useState([]);
  const [subtopics, setSubtopics]   = useState([]);
  const [mastery, setMastery]       = useState(null);
  const [form, setForm]             = useState({ subject: '', chapter: '', topic: '', difficulty: 'medium', num: 5 });
  const [quiz, setQuiz]             = useState(() => (_saved?.phase === 'quiz' || _saved?.phase === 'result') ? _saved.quiz : null);
  const [answers, setAnswers]       = useState(() => _saved?.phase === 'quiz' ? (_saved.answers || {}) : {});
  const [result, setResult]         = useState(() => _saved?.phase === 'result' ? _saved.result : null);
  const [loading, setLoading]       = useState(false);
  const [phase, setPhase]           = useState(() => (_saved?.phase === 'quiz' || _saved?.phase === 'result') ? _saved.phase : 'setup');
  const [history, setHistory]       = useState([]);
  const recentCompletedQuizzes = useMemo(() => history.filter(q => q.completed).slice(0, 5), [history]);
  const [current, setCurrent]       = useState(() => _saved?.phase === 'quiz' ? (_saved.current || 0) : 0);
  const [adaptiveUsed, setAdaptiveUsed] = useState(false);

  // Persist active quiz state to localStorage
  useEffect(() => {
    if (phase === 'quiz' && quiz) {
      localStorage.setItem('aceit_quiz_state', JSON.stringify({ phase, quiz, answers, current }));
    } else if (phase === 'result' && result) {
      localStorage.setItem('aceit_quiz_state', JSON.stringify({ phase, quiz, result }));
    } else if (phase === 'setup') {
      localStorage.removeItem('aceit_quiz_state');
    }
  }, [phase, quiz, answers, current, result]);

  useEffect(() => {
    axios.get(`${API}/syllabus/${userClass}/subjects`, { withCredentials: true })
      .then(r => setSubjects(r.data)).catch(() => {});
    axios.get(`${API}/quiz/history`, { withCredentials: true })
      .then(r => setHistory(r.data)).catch(() => {});
  }, [userClass]);

  // Progress links preselect a verified chapter; they never generate or charge for a quiz.
  useEffect(() => {
    const focus = location.state?.studyFocus;
    if (!focus || !subjects.length || phase !== 'setup') return;
    const key = JSON.stringify(focus);
    if (appliedFocus.current === key) return;
    appliedFocus.current = key;
    let active = true;
    const load = async () => {
      try {
        const target = await resolveStudyTarget({ get: (path, options) => axios.get(API + path, options) }, userClass, focus);
        if (!active) return;
        if (!target.resolved) {
          setFocusNotice('Choose a subject and chapter below to link this older practice result.');
          return;
        }
        const { chapter, chapters: list, subject } = target;
        let topics = [];
        try {
          const response = await axios.get(`${API}/quiz/subtopics`, { params: { class_level: userClass, subject, chapter: chapter.name }, withCredentials: true });
          topics = response.data.subtopics || [];
        } catch { /* Chapter-wide practice remains available. */ }
        if (!active) return;
        setChapters(list); setSubtopics(topics);
        const topic = topics.find(value => normalStudyName(value) === normalStudyName(focus.topic)) || chapter.name;
        setForm(previous => ({ ...previous, subject, chapter: chapter.name, topic, difficulty: 'easy' }));
        setFocusNotice(`Ready to practise ${topic}. Check the settings, then generate when you're ready.`);
      } catch (error) { if (active) setFocusNotice(error.message || 'Choose your topic below.'); }
    };
    load();
    return () => { active = false; appliedFocus.current = null; };
  }, [location.state, subjects, userClass, phase]);

  // Fetch chapters when subject changes
  const onSubjectChange = async (subject) => {
    setForm(p => ({ ...p, subject, chapter: '', topic: '' }));
    setChapters([]);
    setMastery(null);
    if (!subject) return;
    try {
      const { data } = await axios.get(`${API}/syllabus/${userClass}/${encodeURIComponent(subject)}/chapters`, { withCredentials: true });
      setChapters(Array.isArray(data) ? data : data.chapters || []);
    } catch { setChapters([]); }
  };

  // Auto-fill topic and fetch adaptive difficulty + subtopics when chapter changes
  const onChapterChange = async (chapter) => {
    setForm(p => ({ ...p, chapter, topic: chapter }));
    setMastery(null);
    setSubtopics([]);
    if (!chapter) return;
    // Fetch subtopics
    try {
      const { data } = await axios.get(`${API}/quiz/subtopics`, {
        params: { class_level: userClass, subject: form.subject, chapter },
        withCredentials: true,
      });
      const subs = data.subtopics || [];
      setSubtopics(subs);
      setForm(p => ({ ...p, chapter, topic: subs[0] || chapter }));
    } catch { setSubtopics([]); }
    // Fetch adaptive difficulty hint
    try {
      const { data } = await axios.get(`${API}/quiz/topic-mastery`, {
        params: { topic: chapter, subject: form.subject }, withCredentials: true,
      });
      setMastery(data);
      setForm(p => ({ ...p, difficulty: data.recommended_difficulty }));
      setAdaptiveUsed(true);
    } catch { setAdaptiveUsed(false); }
  };

  const generateQuiz = async () => {
    if (!form.subject || !form.topic) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quiz/generate`, {
        class_level: userClass, subject: form.subject,
        chapter: form.chapter || form.topic,
        topic: form.topic, difficulty: form.difficulty, num_questions: form.num,
      }, { withCredentials: true });
      setQuiz(data); setAnswers({}); setCurrent(0); setPhase('quiz');
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || 'Quiz generation failed. Please try again.';
      toast.error(msg, { duration: 5000 });
    }
    setLoading(false);
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quiz/${quiz.quiz_id}/submit`,
        { quiz_id: quiz.quiz_id, answers }, { withCredentials: true });
      // Normalize is_correct → correct
      const normalized = {
        ...data,
        results: (data.results || []).map(r => ({ ...r, correct: r.is_correct ?? r.correct })),
      };
      setResult(normalized);
      setPhase('result');
      // Refresh history
      axios.get(`${API}/quiz/history`, { withCredentials: true }).then(r => setHistory(r.data)).catch(() => {});
      // Refresh mastery hint
      if (form.topic) {
        axios.get(`${API}/quiz/topic-mastery`, { params: { topic: quiz.chapter || quiz.topic, subject: quiz.subject }, withCredentials: true })
          .then(r => setMastery(r.data)).catch(() => {});
      }
    } catch { }
    setLoading(false);
  };

  const reset = () => { setPhase('setup'); setQuiz(null); setResult(null); setAnswers({}); setAdaptiveUsed(false); };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-tour="quiz-workspace">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-heading font-black text-white flex items-center gap-3">
          <Trophy size={28} className="text-amber-400" /> Quiz Arena
        </h1>
        <p className="text-zinc-500 text-sm font-body mt-1">AI-powered quizzes with adaptive difficulty</p>
      </div>

      {location.state?.studyFocus && phase !== 'setup' && <div className="glass-surface p-3 rounded-xl text-sm mb-3">
        <p>Your saved quiz is still open. Finish it, or leave it to open the recommended practice settings.</p>
        <button type="button" className="mt-2 rounded-lg px-3 py-2" onClick={reset}>Open recommended practice</button>
      </div>}
      {focusNotice && <p role="status" className="glass-surface p-3 rounded-xl text-sm mb-4">{focusNotice}</p>}
      <AnimatePresence mode="wait">

        {/* ── SETUP ─────────────────────────────────────────── */}
        {phase === 'setup' && (
          <motion.div key="setup" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-5">
            <div className="glass rounded-2xl p-5 border border-white/10 space-y-3">
              <h2 className="text-white font-heading font-bold mb-1">Configure Your Quiz</h2>

              {/* Class (locked) + Subject */}
              <div className="grid grid-cols-2 gap-3">
                <div className="px-3 py-3 rounded-xl border border-amber-400/30 bg-amber-500/10 text-amber-300 text-sm font-body font-semibold flex items-center gap-2"
                  data-testid="quiz-class-locked">
                  <Sparkles size={14} /> {isNios ? 'Secondary Course' : `Class ${userClass}`}
                </div>
                <div className="relative">
                  <select value={form.subject} onChange={e => onSubjectChange(e.target.value)}
                    data-testid="quiz-subject-select" className={sel}>
                    <option value="">Subject</option>
                    {subjects.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3.5 text-zinc-500 pointer-events-none" />
                </div>
              </div>

              {/* Chapter dropdown (populated after subject) */}
              {chapters.length > 0 && (
                <div className="relative">
                  <select value={form.chapter} onChange={e => onChapterChange(e.target.value)}
                    data-testid="quiz-chapter-select" className={sel}>
                    <option value="">Select Chapter</option>
                    {chapters.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3.5 text-zinc-500 pointer-events-none" />
                </div>
              )}

              {/* Subtopics dropdown (populated after chapter selection) */}
              {subtopics.length > 0 && (
                <div className="relative">
                  <select value={form.topic}
                    onChange={e => setForm(p => ({ ...p, topic: e.target.value }))}
                    data-testid="quiz-subtopic-select" className={sel}>
                    {!subtopics.includes(form.chapter) && <option value={form.chapter}>Whole chapter</option>}
                    {subtopics.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3.5 text-zinc-500 pointer-events-none" />
                </div>
              )}

              {/* Adaptive mastery hint */}
              {mastery && mastery.attempts > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-violet-500/10 border border-violet-500/20 text-xs font-body">
                  <Brain size={13} className="text-violet-400" />
                  <span className="text-zinc-300">
                    Topic mastery: <span className="font-bold" style={{ color: DIFF_COLORS[mastery.recommended_difficulty] }}>{mastery.mastery_pct}%</span>
                  </span>
                  <span className="text-zinc-500">•</span>
                  <span className="text-violet-300">Adaptive difficulty: <strong>{mastery.recommended_difficulty}</strong></span>
                  {adaptiveUsed && <span className="ml-auto px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 text-[10px] font-bold uppercase">Auto-set</span>}
                </div>
              )}

              {/* Difficulty + Questions count */}
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <select value={form.difficulty} onChange={e => { setForm(p => ({ ...p, difficulty: e.target.value })); setAdaptiveUsed(false); }}
                    data-testid="quiz-difficulty-select" className={sel}>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3.5 text-zinc-500 pointer-events-none" />
                </div>
                <div className="relative">
                  <select value={form.num} onChange={e => setForm(p => ({ ...p, num: Number(e.target.value) }))} className={sel}>
                    {[3, 5, 10].map(n => <option key={n} value={n}>{n} Questions</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3.5 text-zinc-500 pointer-events-none" />
                </div>
              </div>

              <button data-ace-primary="true" onClick={generateQuiz} disabled={!form.subject || !form.chapter || loading}
                data-testid="generate-quiz-btn"
                className="w-full py-3 rounded-xl bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-black font-heading font-bold text-sm transition-all flex items-center justify-center gap-2">
                {loading
                  ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  : <><Sparkles size={16} /> Generate AI Quiz</>}
              </button>
            </div>

            {/* Recent history */}
            {recentCompletedQuizzes.length > 0 && (
              <div>
                <h3 className="text-zinc-400 text-sm font-body font-semibold mb-3 uppercase tracking-wider">Recent Scores</h3>
                <div className="space-y-2">
                  {recentCompletedQuizzes.map(q => (
                    <div key={q.quiz_id} className="flex items-center gap-3 p-3 glass-surface rounded-xl border border-white/5">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: q.score >= 70 ? '#10b98120' : '#ef444420' }}>
                        <Trophy size={16} style={{ color: q.score >= 70 ? '#10b981' : '#ef4444' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-body font-medium truncate">{q.topic}</p>
                        <p className="text-zinc-600 text-xs font-body">{q.subject} • Class {q.class_level} • <span className="capitalize">{q.difficulty}</span></p>
                      </div>
                      <span className="font-heading font-bold text-sm" style={{ color: q.score >= 70 ? '#10b981' : '#ef4444' }}>{q.score}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ── QUIZ ──────────────────────────────────────────── */}
        {phase === 'quiz' && quiz && (
          <motion.div key="quiz" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-400 text-sm font-body">Question {current + 1} of {quiz.questions.length}</span>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-heading font-bold capitalize"
                    style={{ background: DIFF_COLORS[form.difficulty] + '20', color: DIFF_COLORS[form.difficulty] }}>
                    {form.difficulty}
                  </span>
                  <button onClick={reset} title="Start new quiz"
                    className="p-1.5 rounded-lg text-zinc-600 hover:text-zinc-300 hover:bg-zinc-800 transition-all" data-testid="exit-quiz-btn">
                    <RotateCcw size={13} />
                  </button>
                  <div className="flex gap-1">
                    {quiz.questions.map((_, i) => (
                      <button key={`qnav-${i}`} onClick={() => setCurrent(i)}
                        className={`w-6 h-1.5 rounded-full transition-all ${i === current ? 'bg-cyan-400' : answers[i] ? 'bg-green-500/60' : 'bg-zinc-700'}`} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-violet-500 rounded-full"
                  animate={{ width: `${((current + 1) / quiz.questions.length) * 100}%` }} />
              </div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                className="glass rounded-2xl p-5 border border-white/10 mb-4">
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-7 h-7 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0 text-cyan-400 text-xs font-heading font-bold">
                    {current + 1}
                  </div>
                  <p className="text-white font-body text-base leading-relaxed">
                    <MathText text={quiz.questions[current]?.question} />
                  </p>
                </div>
                <div className="space-y-2">
                  {quiz.questions[current]?.options?.map((opt, i) => {
                    const letter = opt.charAt(0);
                    const isSelected = answers[current] === letter;
                    return (
                      <button aria-pressed={isSelected} key={opt} onClick={() => !result && setAnswers(p => ({ ...p, [current]: letter }))}
                        data-testid={`quiz-option-${letter}`}
                        className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-body transition-all flex items-center gap-3
                          ${isSelected ? 'border-cyan-500/50 bg-cyan-500/10 text-white' : 'border-white/10 bg-zinc-900/50 text-zinc-300 hover:border-white/20 hover:bg-zinc-800/50'}`}>
                        <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs font-heading font-bold flex-shrink-0
                          ${isSelected ? 'border-cyan-400 text-cyan-400' : 'border-zinc-600 text-zinc-500'}`}>
                          {letter}
                        </span>
                        {opt.slice(3)}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between">
              <button onClick={() => setCurrent(Math.max(0, current - 1))} disabled={current === 0}
                className="px-4 py-2 rounded-xl border border-white/10 text-zinc-400 text-sm font-body disabled:opacity-30 hover:border-white/20 hover:text-white transition-all">
                Previous
              </button>
              {current < quiz.questions.length - 1 ? (
                <button onClick={() => setCurrent(current + 1)} data-testid="next-question-btn"
                  className="px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-body transition-all flex items-center gap-2">
                  Next <ArrowRight size={14} />
                </button>
              ) : (
                <button data-ace-primary="true" onClick={submitQuiz} disabled={Object.keys(answers).length < quiz.questions.length || loading}
                  data-testid="submit-quiz-btn"
                  className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-black font-heading font-bold text-sm transition-all flex items-center gap-2">
                  {loading ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <><CheckCircle size={14} /> Submit Quiz</>}
                </button>
              )}
            </div>
            <p className="text-center text-zinc-600 text-xs font-body mt-3">{Object.keys(answers).length}/{quiz.questions.length} answered</p>
          </motion.div>
        )}

        {/* ── RESULT ────────────────────────────────────────── */}
        {phase === 'result' && result && (
          <motion.div key="result" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
            {/* Score */}
            <div className="text-center py-8 glass rounded-2xl border mb-5"
              style={{ borderColor: result.score >= 70 ? '#10b98130' : '#ef444430', background: result.score >= 70 ? 'rgba(16,185,129,0.04)' : 'rgba(239,68,68,0.04)' }}>
              <div className="text-6xl font-heading font-black mb-2" style={{ color: result.score >= 70 ? '#10b981' : '#ef4444' }}>
                {result.score}%
              </div>
              <p className="text-white font-heading font-bold text-xl mb-1">
                {result.score >= 90 ? 'Outstanding!' : result.score >= 70 ? 'Well Done!' : result.score >= 50 ? 'Good Effort!' : 'Keep Practicing!'}
              </p>
              <p className="text-zinc-500 font-body text-sm">{result.correct_count}/{result.total_questions} correct</p>
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <Zap size={16} className="text-amber-400" />
                <span className="text-amber-400 font-heading font-bold">+{result.xp_earned} XP</span>
                {result.xp_earned === 0 && result.correct_count > 0 && (
                  <span className="text-zinc-500 text-xs font-body ml-1">(answered too fast)</span>
                )}
              </div>
              {result.xp_earned > 0 && (
                <div className="flex items-center justify-center gap-3 mt-1 flex-wrap">
                  {result.perfect_bonus > 0 && (
                    <span className="text-emerald-400 text-xs font-body">+{result.perfect_bonus} perfect</span>
                  )}
                  {result.streak_bonus > 0 && (
                    <span className="text-violet-400 text-xs font-body">+{result.streak_bonus} streak</span>
                  )}
                  {result.improvement_bonus > 0 && (
                    <span className="text-cyan-400 text-xs font-body">+{result.improvement_bonus} improvement</span>
                  )}
                </div>
              )}

              {/* Adaptive next-difficulty hint */}
              {mastery && (
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-body"
                  style={{ background: DIFF_COLORS[mastery.recommended_difficulty] + '15', border: `1px solid ${DIFF_COLORS[mastery.recommended_difficulty]}30` }}>
                  <TrendingUp size={12} style={{ color: DIFF_COLORS[mastery.recommended_difficulty] }} />
                  <span className="text-zinc-300">Next recommended difficulty: </span>
                  <span className="font-bold capitalize" style={{ color: DIFF_COLORS[mastery.recommended_difficulty] }}>{mastery.recommended_difficulty}</span>
                  <span className="text-zinc-500">•</span>
                  <span className="text-zinc-400">Mastery: {mastery.mastery_pct}%</span>
                </div>
              )}
            </div>

            {/* Per-question results */}
            <div className="space-y-3 mb-5">
              {result.results?.map((r, i) => (
                <div key={r.question_id || r.question?.slice(0, 40) || i} className={`p-4 rounded-xl border ${r.correct || r.is_correct ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                  <div className="flex items-start gap-3">
                    {(r.correct || r.is_correct)
                      ? <CheckCircle size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
                      : <XCircle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />}
                    <div className="flex-1">
                      <p className="text-white text-sm font-body leading-relaxed"><MathText text={r.question} /></p>
                      {!(r.correct || r.is_correct) && (
                        <p className="text-zinc-400 text-xs font-body mt-1">
                          Your: <span className="text-red-400">{r.user_answer ?? '–'}</span> • Correct: <span className="text-green-400">{r.correct_answer ?? ''}</span>
                        </p>
                      )}
                      {r.explanation && <p className="text-zinc-500 text-xs font-body mt-1 leading-relaxed"><MathText text={r.explanation} /></p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={reset} data-testid="try-again-btn"
                className="flex-1 py-3 rounded-xl glass border border-white/10 hover:border-white/20 text-white font-heading font-bold text-sm transition-all flex items-center justify-center gap-2">
                <RotateCcw size={16} /> New Quiz
              </button>
              {mastery && result.score < 70 && (
                <button onClick={() => {
                  setForm(p => ({ ...p, difficulty: 'easy' })); reset();
                }}
                  className="flex-1 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-heading font-bold text-sm transition-all flex items-center justify-center gap-2">
                  <BookOpen size={16} /> Retry (Easy)
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}



